import React, { useState, useEffect, useCallback } from "react";
import { Briefcase, MapPin, Plus, Pencil, Trash2, Globe2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard, ccBtnPink } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const PERMISSIONS = [
  ["can_manage_users", "Manage Users"],
  ["can_moderate_store", "Moderate Store"],
  ["can_assign_regions", "Assign Regions"],
  ["can_view_analytics", "View Analytics"],
  ["can_manage_onboarding", "Manage Onboarding"],
  ["can_moderate_accounts", "Moderate Accounts"],
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const TIER_STYLE = {
  national: "bg-[#374151] text-white",
  regional: "bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E]",
  local: "bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244]",
};

// Dynamic job position builder + regional scope assignment (super admin control)
export default function MasterHierarchy({ staff = [], onChanged }) {
  const [positions, setPositions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [form, setForm] = useState({ title: "", permissions: [], scope_tier: "national" });
  const [assignForm, setAssignForm] = useState({ position_id: "", scope_tier: "national", scope_states: [], scope_localities: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/positions`, { credentials: "include" });
      if (res.ok) setPositions((await res.json()).positions);
    } catch (err) {
      console.error("Positions load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (pos) => {
    setEditing(pos || null);
    setForm(pos ? { title: pos.title, permissions: [...pos.permissions], scope_tier: pos.scope_tier } : { title: "", permissions: [], scope_tier: "national" });
    setShowForm(true);
  };

  const savePosition = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(editing ? `${API}/api/master/positions/${editing.id}` : `${API}/api/master/positions`, {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({})); // gateway HTML error pages (502/503 during deploys) are not JSON
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : `Save failed (server ${res.status}) — if a publish is running, try again in a minute`);
      toast.success(editing ? "Position updated." : "Position created.");
      setShowForm(false);
      load();
      window.dispatchEvent(new Event("nt-positions-changed"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deletePosition = async (pid) => {
    try {
      const res = await fetch(`${API}/api/master/positions/${pid}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Position deleted — staff unassigned from it.");
      setConfirmDelete(null);
      load();
      window.dispatchEvent(new Event("nt-positions-changed"));
      onChanged?.();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openAssign = (s) => {
    setAssigning(assigning === s.id ? null : s.id);
    setAssignForm({
      position_id: s.position_id || "",
      scope_tier: s.scope_tier || "national",
      scope_states: s.scope_states || [],
      scope_localities: (s.scope_localities || []).join(", "),
    });
  };

  const saveAssign = async (s) => {
    try {
      const res = await fetch(`${API}/api/master/staff/${s.id}/assign`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          position_id: assignForm.position_id,
          scope_tier: assignForm.scope_tier,
          scope_states: assignForm.scope_tier === "regional" ? assignForm.scope_states : [],
          scope_localities: assignForm.scope_tier === "local" ? assignForm.scope_localities.split(",").map((x) => x.trim()).filter(Boolean) : [],
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      toast.success(`Scope assigned to ${s.name}.`);
      setAssigning(null);
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleState = (st) =>
    setAssignForm((f) => ({ ...f, scope_states: f.scope_states.includes(st) ? f.scope_states.filter((x) => x !== st) : [...f.scope_states, st] }));

  return (
    <section className={`${ccCard} p-6`} data-testid="hierarchy-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-[#5CB3DC]" />
          </span>
          Admin Hierarchy & Regions
        </h3>
        <button onClick={() => openForm(null)} data-testid="position-add-btn" className={ccBtnPink}>
          <Plus className="w-4 h-4 inline mr-1 -mt-0.5" /> New Position
        </button>
      </div>

      {showForm && (
        <form onSubmit={savePosition} className="mb-5 p-5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl space-y-4" data-testid="position-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={ccLabel}>Job Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ccInput} placeholder="e.g. Regional Director" data-testid="position-title-input" />
            </div>
            <div>
              <label className={ccLabel}>Default Scope Tier</label>
              <select value={form.scope_tier} onChange={(e) => setForm({ ...form, scope_tier: e.target.value })} className={ccInput} data-testid="position-tier-select">
                <option value="national">National / System-Wide</option>
                <option value="regional">Regional (States)</option>
                <option value="local">Local / Community</option>
              </select>
            </div>
          </div>
          <div>
            <label className={ccLabel}>Permission Matrix</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PERMISSIONS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-xs font-semibold text-[#374151] cursor-pointer" data-testid={`position-perm-${key}`}>
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(key)}
                    onChange={(e) =>
                      setForm({ ...form, permissions: e.target.checked ? [...form.permissions, key] : form.permissions.filter((p) => p !== key) })
                    }
                    className="accent-[#E5A0BC]"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className={ccBtnPink} data-testid="position-save-btn">{editing ? "Save Position" : "Create Position"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-xs font-bold" data-testid="position-cancel-btn">Cancel</button>
          </div>
        </form>
      )}

      {positions.length === 0 && !showForm ? (
        <p className="text-xs text-[#9CA3AF] font-medium mb-5" data-testid="positions-empty">No custom positions yet — create job titles with permission matrices as the team scales.</p>
      ) : (
        <div className="space-y-2.5 mb-6">
          {positions.map((p) => (
            <div key={p.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap" data-testid={`position-row-${p.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-[#374151] flex items-center gap-2">
                  {p.title}
                  <span className={`px-2 py-0.5 text-[9px] font-extrabold uppercase rounded-full ${TIER_STYLE[p.scope_tier]}`} data-testid={`position-tier-${p.id}`}>{p.scope_tier}</span>
                </p>
                <div className="flex gap-1.5 flex-wrap mt-1.5">
                  {p.permissions.map((perm) => (
                    <span key={perm} className="px-2 py-0.5 bg-white border border-[#E6F2FA] text-[#3D7E9E] text-[9px] font-bold rounded-full">{perm.replace("can_", "").replaceAll("_", " ")}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => openForm(p)} data-testid={`position-edit-${p.id}`} className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#5CB3DC] hover:bg-[#E6F2FA] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {confirmDelete === p.id ? (
                  <button onClick={() => deletePosition(p.id)} data-testid={`position-delete-confirm-${p.id}`} className="px-3 py-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full">Confirm?</button>
                ) : (
                  <button onClick={() => setConfirmDelete(p.id)} data-testid={`position-delete-${p.id}`} className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-rose-400 hover:bg-rose-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-3 flex items-center gap-1.5">
        <Globe2 className="w-3.5 h-3.5" /> Regional Assignment
      </h4>
      <div className="space-y-2.5">
        {staff.map((s) => (
          <div key={s.id} className="p-4 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl" data-testid={`assign-row-${s.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2.5 min-w-0">
                <p className="text-xs font-extrabold text-[#374151]">{s.name}</p>
                <span className={`px-2 py-0.5 text-[9px] font-extrabold uppercase rounded-full ${TIER_STYLE[s.scope_tier || "national"]}`} data-testid={`assign-tier-${s.id}`}>
                  {s.scope_tier || "national"}
                </span>
                {(s.scope_states || []).length > 0 && (
                  <span className="text-[10px] font-bold text-[#5CB3DC] flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {s.scope_states.join(", ")}
                  </span>
                )}
                {(s.scope_localities || []).length > 0 && (
                  <span className="text-[10px] font-bold text-[#A08244] flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {s.scope_localities.join(", ")}
                  </span>
                )}
              </div>
              <button onClick={() => openAssign(s)} data-testid={`assign-btn-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-[10px] font-bold hover:bg-[#E6F2FA] transition-colors">
                <ChevronDown className="w-3 h-3" /> {assigning === s.id ? "Close" : "Assign Scope"}
              </button>
            </div>
            {assigning === s.id && (
              <div className="mt-3 pt-3 border-t border-[#FCE6E9] space-y-3" data-testid={`assign-form-${s.id}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={ccLabel}>Job Position</label>
                    <select value={assignForm.position_id} onChange={(e) => setAssignForm({ ...assignForm, position_id: e.target.value })} className={ccInput} data-testid={`assign-position-${s.id}`}>
                      <option value="">No custom position</option>
                      {positions.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={ccLabel}>Coverage Tier</label>
                    <select
                      value={assignForm.scope_tier}
                      onChange={(e) => setAssignForm({ ...assignForm, scope_tier: e.target.value, scope_states: [], scope_localities: "" })}
                      className={ccInput}
                      data-testid={`assign-tier-select-${s.id}`}
                    >
                      <option value="national">National / System-Wide</option>
                      <option value="regional">Regional (States)</option>
                      <option value="local">Local / Community</option>
                    </select>
                  </div>
                </div>
                {assignForm.scope_tier === "regional" && (
                  <div>
                    <label className={ccLabel}>Covered States</label>
                    <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 max-h-32 overflow-y-auto p-2 bg-white border border-[#FCE6E9] rounded-xl" data-testid={`assign-states-${s.id}`}>
                      {US_STATES.map((st) => (
                        <button
                          type="button"
                          key={st}
                          onClick={() => toggleState(st)}
                          className={`px-1 py-1 text-[10px] font-bold rounded-lg border transition-colors ${
                            assignForm.scope_states.includes(st) ? "bg-[#B3E0F2] border-[#9ACCE0] text-[#374151]" : "bg-[#FFF9F2] border-[#FCE6E9] text-[#9CA3AF]"
                          }`}
                          data-testid={`assign-state-${s.id}-${st}`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {assignForm.scope_tier === "local" && (
                  <div>
                    <label className={ccLabel}>Localities (comma-separated cities/neighborhoods)</label>
                    <input value={assignForm.scope_localities} onChange={(e) => setAssignForm({ ...assignForm, scope_localities: e.target.value })} className={ccInput} placeholder="e.g. Madison, Dane County" data-testid={`assign-localities-${s.id}`} />
                  </div>
                )}
                <button onClick={() => saveAssign(s)} className={ccBtnPink} data-testid={`assign-save-${s.id}`}>Save Assignment</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
